import React from 'react';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';

import { TourOffer, TourOfferOperations } from '~/types/tours/tourOffer';

import NativeTourCard from './NativeTourCard';
import NativeTourEntries from './NativeTourEntries';
import NativeTourSupplierLink from './NativeTourSupplierLink';

interface Props {
  operations: TourOfferOperations | null | undefined;
  services: TourOffer['services'];
  /** Where the tour comes from: Salesforce when it has a legacy tour. */
  isSalesforceTour: boolean;
}

/** Who runs the tour and how to reach them. Read only: Salesforce owns these. */
export default function NativeTourOperationsSection({ operations, services, isSalesforceTour }: Props) {
  if (!operations) {
    return <Alert severity="info">This tour has no operations details yet. They come from Salesforce.</Alert>;
  }
  const { contacts, notes } = operations;
  const suppliers = services
    .filter((service) => service.linkedTourOfferId === null)
    .map((service) => service.supplier)
    .filter((supplier, index, all) => all.findIndex((other) => other.id === supplier.id) === index);

  return (
    <Stack spacing={3}>
      <Alert severity="info">These come from Salesforce. Change them there, and they update on the next import.</Alert>
      <NativeTourCard title="Suppliers and selling account" description="Who supplies the tour, and who sells it.">
        <Stack spacing={2}>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              Suppliers (from their contracts)
            </Typography>
            {suppliers.length === 0 ? (
              <Typography>No suppliers yet.</Typography>
            ) : (
              <Stack direction="row" gap={1} flexWrap="wrap">
                {suppliers.map((supplier) => (
                  <NativeTourSupplierLink key={supplier.id} supplierId={supplier.id} name={supplier.name} />
                ))}
              </Stack>
            )}
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              Selling account
            </Typography>
            {operations.vendorId ? (
              <Button
                variant="text"
                endIcon={<OpenInNewIcon fontSize="small" />}
                href={`/vendors/${operations.vendorId}`}
                target="_blank"
                rel="noreferrer"
              >
                {operations.vendorId}
              </Button>
            ) : (
              <Typography>None</Typography>
            )}
          </Box>
          <NativeTourEntries entries={[{ label: 'Source', value: isSalesforceTour ? 'Salesforce' : 'Admin' }]} />
        </Stack>
      </NativeTourCard>
      <NativeTourCard title="DMC contacts" description="Where bookings and manifests go.">
        <NativeTourEntries
          entries={[
            { label: 'Reservations email', value: contacts?.reservationsEmail },
            { label: 'Manifest emails', value: contacts?.manifestEmails.join('\n') },
            { label: 'System email', value: contacts?.systemEmail },
            { label: 'Booking email', value: contacts?.bookingEmail },
            { label: 'Booking phone', value: contacts?.bookingPhone },
            { label: 'Emergency phone', value: contacts?.emergencyPhone },
          ]}
        />
      </NativeTourCard>
      <NativeTourCard title="Escalation" description="Who to call when something goes wrong on tour.">
        <NativeTourEntries
          entries={[
            { label: 'Name', value: contacts?.escalation.name },
            { label: 'Email', value: contacts?.escalation.email },
            { label: 'Phone', value: contacts?.escalation.phone },
          ]}
        />
      </NativeTourCard>
      <NativeTourCard title="LE team">
        <NativeTourEntries
          entries={[
            { label: 'Merchant account manager', value: contacts?.team.merchantAccountManager },
            { label: 'Opportunity owner', value: contacts?.team.opportunityOwner },
            { label: 'Concierge manager', value: contacts?.team.conciergeManager },
          ]}
        />
      </NativeTourCard>
      <NativeTourCard
        title="Notes for the team"
        description="For customer service and the DMC, never shown to customers."
      >
        <NativeTourEntries
          entries={[
            { label: 'Customer experience', value: notes?.customerExperience },
            { label: 'Vehicles', value: notes?.vehicles },
            { label: 'Check-in and check-out', value: notes?.checkInOutTime },
            { label: 'Additional DMC instructions', value: notes?.additionalInstructions },
            { label: 'Special instructions', value: operations.specialInstructions },
          ]}
        />
      </NativeTourCard>
    </Stack>
  );
}
